import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import ChatWidget from './components/ChatWidget';

// Pages (placeholders for now)
const Home = React.lazy(() => import('./pages/Home'));
const Products = React.lazy(() => import('./pages/Products'));
const Skriot = React.lazy(() => import('./pages/Skriot'));
const WorkLink = React.lazy(() => import('./pages/WorkLink'));
const About = React.lazy(() => import('./pages/About'));
const Careers = React.lazy(() => import('./pages/Careers'));
const Contact = React.lazy(() => import('./pages/Contact'));
const CorvelaCX = React.lazy(() => import('./pages/CorvelaCX'));

function DarkModeToggle() {
  const [dark, setDark] = useState(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  useEffect(() => {
    const saved = localStorage.getItem('theme');
    if (saved) setDark(saved === 'dark');
  }, []);

  return (
    <button
      className="px-3 py-1 rounded border border-gray-300 dark:border-gray-700 ml-4"
      onClick={() => setDark((d) => !d)}
      aria-label="Toggle dark mode"
    >
      {dark ? '🌙 Dark' : '☀️ Light'}
    </button>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const desktopNavLinks = [
    { to: '/', label: 'Home' },
    { to: '/products', label: 'Products' },
    { to: '/about', label: 'About' },
    { to: '/careers', label: 'Careers' },
    { to: '/contact', label: 'Contact' }
  ];
  
  const mobileNavLinks = [
    { to: '/', label: 'Home' },
    { to: '/products', label: 'Products' },
    { to: '/skriot', label: 'Skriot' },
    { to: '/work-link', label: 'Work Link (Beta)' },
    { to: '/about', label: 'About' },
    { to: '/careers', label: 'Careers' },
    { to: '/contact', label: 'Contact' },
  ];
  
  return (
    <nav className="flex items-center justify-between py-4 px-6 bg-white dark:bg-black shadow relative">
      {/* Hamburger Icon - only visible on mobile, top left */}
      <button
        className="md:hidden flex flex-col justify-center items-center w-10 h-10 focus:outline-none absolute left-4 top-1/2 transform -translate-y-1/2 z-60"
        onClick={() => setOpen(!open)}
        aria-label="Open menu"
      >
        <span className="block w-7 h-1 rounded mb-1 bg-black dark:bg-white"></span>
        <span className="block w-7 h-1 rounded mb-1 bg-black dark:bg-white"></span>
        <span className="block w-7 h-1 rounded bg-black dark:bg-white"></span>
      </button>
      {/* Logo or Brand Name - centered on mobile, left on desktop */}
      <div className="flex-1 flex items-center justify-center md:justify-start">
        <Link to="/" className="text-xl font-bold text-black dark:text-white">Corvela</Link>
      </div>
      {/* Desktop Navigation - only visible on md screens and up */}
      <div className="hidden md:flex items-center space-x-8">
        {desktopNavLinks.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={`text-base text-black dark:text-white hover:text-gray-600 dark:hover:text-gray-300 ${location.pathname === link.to ? 'font-semibold underline' : ''}`}
          >
            {link.label}
          </Link>
        ))}
      </div>
      {/* Dark Mode Toggle */}
      <div className="ml-4 md:ml-8">
        <DarkModeToggle />
      </div>
      {/* Mobile Menu & Backdrop - only appears when hamburger is clicked */}
      {open && (
        <>
          {/* Backdrop overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-40"
            onClick={() => setOpen(false)}
            aria-label="Close menu backdrop"
          ></div>
          {/* Drawer */}
          <div
            className="fixed top-0 left-0 h-full w-64 bg-white dark:bg-black shadow-lg z-50 transform transition-transform duration-300"
            style={{ transition: 'transform 0.3s' }}
          >
            <button
              className="absolute top-4 right-4 text-2xl focus:outline-none"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
            >
              &times;
            </button>
            <div className="flex flex-col mt-16 space-y-4 px-6">
              {mobileNavLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={`text-lg px-2 py-2 rounded text-black dark:text-white hover:bg-gray-200 dark:hover:bg-gray-800 ${location.pathname === link.to ? 'font-semibold underline' : ''}`}
                >
                  {link.label}
                </Link>
              ))}
              {/* Dark Mode Toggle for mobile drawer */}
              <div className="mt-10 mb-4 text-center">
                <DarkModeToggle />
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}

function App() {
  return (
    <Router>
      <div className="flex flex-col min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-300">
        <Navbar />
        <main className="flex-grow">
          <React.Suspense fallback={<div className="p-8">Loading...</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/skriot" element={<Skriot />} />
              <Route path="/work-link" element={<WorkLink />} />
              <Route path="/about" element={<About />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/corvela-cx" element={<CorvelaCX />} />
            </Routes>
          </React.Suspense>
        </main>
        <ChatWidget />
      </div>
    </Router>
  );
}

export default App;
